import { lazy, Suspense, useEffect, useState } from "react";
import { Switch, Route, Router as WouterRouter, Redirect } from "wouter";

const LandingPage = lazy(() => import("@/pages/LandingPage"));
const AuthenticatedApp = lazy(() => import("./AuthenticatedApp"));

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

function RouteFallback() {
  return <div className="min-h-screen bg-slate-950" aria-busy="true" />;
}

// Cheap, no-firebase peek into IndexedDB to detect a persisted Firebase auth
// user. If found we lazily load the full authenticated app to redirect to
// /dashboard. Anonymous landing-page visitors never download firebase or the
// authenticated app shell, keeping the landing critical path tiny.
function detectPersistedFirebaseUser(): Promise<boolean> {
  return new Promise((resolve) => {
    if (typeof indexedDB === "undefined") {
      resolve(false);
      return;
    }
    let settled = false;
    const finish = (result: boolean) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };
    setTimeout(() => finish(false), 1500);
    try {
      const req = indexedDB.open("firebaseLocalStorageDb");
      req.onerror = () => finish(false);
      req.onsuccess = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains("firebaseLocalStorage")) {
          db.close();
          finish(false);
          return;
        }
        try {
          const tx = db.transaction("firebaseLocalStorage", "readonly");
          const store = tx.objectStore("firebaseLocalStorage");
          const countReq = store.count();
          countReq.onsuccess = () => {
            db.close();
            finish(countReq.result > 0);
          };
          countReq.onerror = () => {
            db.close();
            finish(false);
          };
        } catch {
          db.close();
          finish(false);
        }
      };
    } catch {
      finish(false);
    }
  });
}

function HomeRoute() {
  const [signedIn, setSignedIn] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    detectPersistedFirebaseUser().then((found) => {
      if (!cancelled) setSignedIn(found);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (signedIn === true) return <Redirect to="/dashboard" />;
  return <LandingPage />;
}

function App() {
  return (
    <WouterRouter base={basePath}>
      <Suspense fallback={<RouteFallback />}>
        <Switch>
          <Route path="/" component={HomeRoute} />
          <Route>
            <AuthenticatedApp />
          </Route>
        </Switch>
      </Suspense>
    </WouterRouter>
  );
}

export default App;
